import React from 'react'
import { X } from 'lucide-react'
import { Input, Label, Modal } from '@/shared/ui'
import { useCommunityDraftAttachments } from '@/modules/comunidade/hooks/useCommunityDraftAttachments'
import { extractCommunityMessagePlainText } from '@/modules/comunidade/utils/communityMessageContent'
import type { AnalysisPostAttachment } from '../services/analysisPostAttachmentsService'
import { AnalysisPostAttachmentsEditor } from './AnalysisPostAttachmentsEditor'
import { AnalysisPostComposer } from './AnalysisPostComposer'
import { AnalysisOpportunityLinkField } from './AnalysisOpportunityLinkField'
import { normalizeLinkedOpportunityId } from '../utils/normalizeLinkedOpportunityId'
import { normalizeLinkedTimelineRootId } from '../utils/normalizeLinkedTimelineRootId'
import { AnalysisCourtesyField } from './AnalysisCourtesyField'
import { AnalysisLinkedOpportunityNotice } from './AnalysisLinkedOpportunityNotice'
import { AnalysisTimelineLinkField } from './AnalysisTimelineLinkField'

export type NewAnalysisModalSubmitParams = {
  title: string
  html: string
  newAttachments: File[]
  removeAttachmentIds: number[]
  linkedOpportunityId: number | null
  linkedTimelineRootId: number | null
  courtesyEnabled: boolean
}

type NewAnalysisModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit?: (params: NewAnalysisModalSubmitParams) => void | Promise<void>
  analysisId?: number
  initialTitle?: string
  initialHtml?: string
  initialAttachments?: AnalysisPostAttachment[]
  initialLinkedOpportunityId?: number | null
  initialLinkedTimelineRootId?: number | null
  initialCourtesyEnabled?: boolean
  mode?: 'create' | 'edit'
}

export function NewAnalysisModal({
  open,
  onOpenChange,
  onSubmit,
  analysisId,
  initialTitle,
  initialHtml,
  initialAttachments,
  initialLinkedOpportunityId,
  initialLinkedTimelineRootId,
  initialCourtesyEnabled,
  mode = 'create',
}: NewAnalysisModalProps) {
  const [title, setTitle] = React.useState(initialTitle || '')
  const [html, setHtml] = React.useState(initialHtml || '')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [linkEnabled, setLinkEnabled] = React.useState(false)
  const [linkedOpportunityId, setLinkedOpportunityId] = React.useState<number | null>(null)
  const [timelineLinkEnabled, setTimelineLinkEnabled] = React.useState(false)
  const [linkedTimelineRootId, setLinkedTimelineRootId] = React.useState<number | null>(null)
  const [courtesyEnabled, setCourtesyEnabled] = React.useState(false)
  const [existingAttachments, setExistingAttachments] = React.useState<AnalysisPostAttachment[]>(
    Array.isArray(initialAttachments) ? initialAttachments : [],
  )
  const [removeAttachmentIds, setRemoveAttachmentIds] = React.useState<number[]>([])

  const {
    attachments: draftAttachments,
    addFiles: addDraftFiles,
    removeAttachment: removeDraftAttachment,
    clear: clearDraftAttachments,
    error: attachmentError,
  } = useCommunityDraftAttachments()

  const isEdit = mode === 'edit'

  const hasContent = React.useMemo(() => {
    const text = extractCommunityMessagePlainText(html || '')
    return !!String(text || '').trim()
  }, [html])

  const canSubmit = React.useMemo(() => {
    const hasTitle = !!title.trim()
    const hasOpportunitySelection = !linkEnabled || linkedOpportunityId !== null
    const hasTimelineSelection = !timelineLinkEnabled || linkedTimelineRootId !== null
    return hasTitle && hasContent && hasOpportunitySelection && hasTimelineSelection
  }, [hasContent, linkEnabled, linkedOpportunityId, timelineLinkEnabled, linkedTimelineRootId, title])

  const reset = React.useCallback(() => {
    setTitle(initialTitle || '')
    setHtml(initialHtml || '')
    setError(null)
    const initialLink = normalizeLinkedOpportunityId(initialLinkedOpportunityId)
    const initialTimeline = normalizeLinkedTimelineRootId(initialLinkedTimelineRootId)
    const useTimeline = initialTimeline !== null && initialLink === null
    setLinkEnabled(initialLink !== null)
    setLinkedOpportunityId(initialLink)
    setTimelineLinkEnabled(useTimeline)
    setLinkedTimelineRootId(useTimeline ? initialTimeline : null)
    setCourtesyEnabled(Boolean(initialCourtesyEnabled) && initialLink !== null)
    setExistingAttachments(Array.isArray(initialAttachments) ? initialAttachments : [])
    setRemoveAttachmentIds([])
    clearDraftAttachments()
  }, [
    clearDraftAttachments,
    initialAttachments,
    initialCourtesyEnabled,
    initialHtml,
    initialLinkedOpportunityId,
    initialLinkedTimelineRootId,
    initialTitle,
  ])

  React.useEffect(() => {
    if (!open) return
    reset()
  }, [open, reset])

  const handleClose = React.useCallback(() => {
    onOpenChange(false)
    reset()
  }, [onOpenChange, reset])

  const handleRemoveExisting = React.useCallback((id: number) => {
    setExistingAttachments((prev) => prev.filter((att) => att.id !== id))
    setRemoveAttachmentIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
  }, [])

  React.useEffect(() => {
    if (!linkEnabled || linkedOpportunityId == null || timelineLinkEnabled) {
      setCourtesyEnabled(false)
    }
  }, [linkEnabled, linkedOpportunityId, timelineLinkEnabled])

  const handleOpportunityEnabledChange = React.useCallback(
    (next: boolean) => {
      setLinkEnabled(next)
      if (next) {
        setTimelineLinkEnabled(false)
        setLinkedTimelineRootId(null)
      }
    },
    [],
  )

  const handleTimelineEnabledChange = React.useCallback(
    (next: boolean) => {
      setTimelineLinkEnabled(next)
      if (next) {
        setLinkEnabled(false)
        setLinkedOpportunityId(null)
        setCourtesyEnabled(false)
      }
    },
    [],
  )

  const handleSubmit = React.useCallback(async () => {
    const trimmedTitle = title.trim()
    const trimmedHtml = String(html || '').trim()
    const newFiles = draftAttachments.map((a) => a.file).filter(Boolean)

    if (!trimmedTitle) {
      setError('Preencha o título antes de enviar.')
      return
    }

    if (!hasContent) {
      setError('Preencha a descrição antes de enviar.')
      return
    }

    if (linkEnabled && linkedOpportunityId === null) {
      setError('Selecione uma oportunidade confirmada ou desative o vinculo.')
      return
    }

    if (timelineLinkEnabled && linkedTimelineRootId === null) {
      setError('Selecione um post publicado ou desative o vinculo da timeline.')
      return
    }

    setSaving(true)
    setError(null)
    try {
      if (onSubmit) {
        await onSubmit({
          title: trimmedTitle,
          html: trimmedHtml,
          newAttachments: newFiles,
          removeAttachmentIds,
          linkedOpportunityId: linkEnabled ? linkedOpportunityId : null,
          linkedTimelineRootId: timelineLinkEnabled ? linkedTimelineRootId : null,
          courtesyEnabled: linkEnabled ? courtesyEnabled : false,
        })
      }
      onOpenChange(false)
      reset()
    } catch (e: any) {
      setError(e?.message || 'Falha ao salvar. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }, [
    draftAttachments,
    existingAttachments.length,
    hasContent,
    html,
    linkEnabled,
    linkedOpportunityId,
    timelineLinkEnabled,
    linkedTimelineRootId,
    onOpenChange,
    onSubmit,
    courtesyEnabled,
    removeAttachmentIds,
    reset,
    title,
  ])

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          handleClose()
        } else {
          onOpenChange(true)
        }
      }}
      className="w-full max-w-2xl p-6"
    >
      <div className="space-y-4">
        <div className="relative pr-10">
          <button
            type="button"
            aria-label="Fechar"
            disabled={saving}
            onClick={handleClose}
            className="absolute right-0 top-0 rounded p-1 text-foreground/70 hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
          <h2 className="text-lg font-semibold">{isEdit ? 'Editar análise' : 'Nova análise'}</h2>
          <p className="text-xs text-foreground/60">
            {isEdit
              ? 'Altere o título, o conteúdo e os anexos da análise de mercado.'
              : 'Crie uma nova análise de mercado para os membros autorizados.'}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="analysis-title">Título</Label>
          <Input
            id="analysis-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex.: Panorama de mercado BTC/ETH para a semana"
          />
        </div>

        <AnalysisOpportunityLinkField
          enabled={linkEnabled}
          selectedOpportunityId={linkedOpportunityId}
          onEnabledChange={handleOpportunityEnabledChange}
          onSelectedOpportunityIdChange={setLinkedOpportunityId}
          disabled={saving}
          showExistingAnalysisBadge={!isEdit}
          excludeAnalysisId={analysisId ?? null}
        />

        <AnalysisTimelineLinkField
          enabled={timelineLinkEnabled}
          selectedRootId={linkedTimelineRootId}
          onEnabledChange={handleTimelineEnabledChange}
          onSelectedRootIdChange={setLinkedTimelineRootId}
          disabled={saving}
          excludeAnalysisId={analysisId ?? null}
        />

        <AnalysisLinkedOpportunityNotice
          opportunityId={linkedOpportunityId}
          excludeAnalysisId={analysisId ?? null}
          visible={linkEnabled && linkedOpportunityId != null}
        />

        <AnalysisCourtesyField
          visible={linkEnabled}
          value={courtesyEnabled}
          linkedOpportunityId={linkedOpportunityId}
          disabled={saving}
          onChange={setCourtesyEnabled}
        />

        <div className="space-y-2">
          <Label>Descrição</Label>

          {attachmentError && <div className="text-[11px] text-red-400">{attachmentError}</div>}

          <AnalysisPostAttachmentsEditor
            existingAttachments={existingAttachments}
            onRemoveExisting={handleRemoveExisting}
            draftAttachments={draftAttachments}
            onRemoveDraft={removeDraftAttachment}
          />

          <AnalysisPostComposer
            value={html}
            onChange={setHtml}
            placeholder="Digite os termos aqui..."
            onAddFiles={addDraftFiles}
            disabled={saving}
            onSubmit={handleSubmit}
            submitDisabled={saving || !canSubmit}
          />

          <p className="text-xs text-foreground/60">
            Use o editor para formatar o texto, anexar arquivos/imagens, inserir emojis e mencionar usuários com @.
          </p>
        </div>

        {error && <div className="text-xs text-destructive">{error}</div>}
      </div>
    </Modal>
  )
}

